import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import { dayjsFromDecimalHours } from "../../../utils/decimalHoursToHHMM";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PaymentType } from "../../../utils/enum";
import BaseHourMinutePicker from "../../../components/ui/BaseHourMinutePicker";
import { useEffect, useRef, useState } from "react";
import {
  displayFormattedTotalTimelog,
  toBase100Hours,
} from "../../../utils/helpers";
import { useMutation } from "@tanstack/react-query";
import { updateWorkOrderTimeLog } from "../../../services/api/workOrder";
import { useParams } from "react-router-dom";
import { cloneDeep, isEqual, omit } from "lodash";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";

const validationSchema = Yup.object({
  clientName: Yup.string().required("Client Name is required"),
  manager: Yup.string().required("Manager is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().notRequired(),
  zipCode: Yup.string().required("Zip Code is required"),
  country: Yup.string().required("Country is required"),
  phone: Yup.string().required("Phone is required"),
  email: Yup.string()
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email format",
    ),
});

const EditClientApprovalTimeLogModal = ({
  isEditClientApprovalTimeLogModalOpen,
  setIsEditClientApprovalTimeLogModalOpen,
  payment,
  refetchworkOrderPaymentData,
}) => {
  const { id: workOrderId } = useParams();
  const [isShowNBH, setIsShowNBH] = useState(
    payment?.nonBusinessHours || payment.nonBusinessHourRate ? true : false,
  );
  const {
    isPending: isUpdateWorkOrderTimeLogApiLoading,
    error: isUpdateWorkOrderTimeLogApiError,
    data: updatedTimeLog,
    mutateAsync: updateWorkOrderTimeLogMutation,
  } = useMutation({
    mutationFn: updateWorkOrderTimeLog,
    enabled: false,
  });

  let minimumHours = null;

  if (payment.paymentType === "HOURLY") {
    minimumHours = parseFloat(
      dayjsFromDecimalHours(payment.minimumHours).format("HH.mm"),
    );
  } else if (payment.paymentType === "HALF_DAY") {
    minimumHours = parseFloat(dayjsFromDecimalHours(4).format("HH.mm"));
  } else if (payment.paymentType === "FULL_DAY") {
    minimumHours = parseFloat(dayjsFromDecimalHours(8).format("HH.mm"));
  } else if (payment.paymentType === "FTH") {
    minimumHours = parseFloat(dayjsFromDecimalHours(2).format("HH.mm"));
  } else if (payment.paymentType === "CUSTOM_HOURS") {
    minimumHours = parseFloat(
      dayjsFromDecimalHours(payment.customHours).format("HH.mm"),
    );
  }

  const nonBusinessHours = parseFloat(
    dayjsFromDecimalHours(payment?.nonBusinessHours).format("HH.mm"),
  );
  const additionalHours = parseFloat(
    dayjsFromDecimalHours(payment?.additionalHours).format("HH.mm"),
  );

  const initialValues = {
    minimumHours: {
      hour: parseInt(minimumHours?.toFixed(2)?.split(".")[0]),
      minute: parseInt(minimumHours?.toFixed(2)?.split(".")[1]),
    },

    amount:
      (payment.paymentType === "FIXED" && payment.fixedAmount) ||
      (payment.paymentType === "HOURLY" && payment.hourlyRate) ||
      (payment.paymentType === "HALF_DAY" && payment.halfDayRate) ||
      (payment.paymentType === "FULL_DAY" && payment.fullDayRate) ||
      (payment.paymentType === "CUSTOM_HOURS" && payment.customHoursRate) ||
      (payment.paymentType === "FTH" && payment.fthRate),

    totalLabourCost:
      payment.paymentType === "CUSTOM_HOURS"
        ? payment.customHoursRate
        : payment.totalLabourCost,
    nonBusinessTotalLabourCost: payment.nonBusinessTotalLabourCost || 0,

    nonBusinessHourRate: payment?.nonBusinessHourRate || 0,
    nonBusinessHours: {
      hour: parseInt(nonBusinessHours?.toFixed(2)?.split(".")[0]) || 0,
      minute: parseInt(nonBusinessHours?.toFixed(2)?.split(".")[1]) || 0,
    },
    additionalHours: {
      hour: parseInt(additionalHours?.toFixed(2)?.split(".")[0]) || 0,
      minute: parseInt(additionalHours?.toFixed(2)?.split(".")[1]) || 0,
    },
    additionalHourlyRate: payment.additionalHourlyRate,
    totalNonBusinessPayment: payment.totalNonBusinessPayment,
  };
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    // validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSaveTimeLogButton(values);
    },
  });

  const ignoredFields = [
    "totalLabourCost",
    "nonBusinessTotalLabourCost",
    "totalNonBusinessPayment",
  ];

  const filteredValues = omit(formik.values, ignoredFields);
  const filteredInitial = omit(initialValues, ignoredFields);

  const isFormChanged = !isEqual(filteredValues, filteredInitial);

  useEffect(() => {
    const combinedHoursMinutes = toBase100Hours({
      hours: formik.values.minimumHours.hour,
      minutes: formik.values.minimumHours.minute,
    });
    let totalLabourCost = null;
    if (payment.paymentType === "HOURLY") {
      totalLabourCost = combinedHoursMinutes * payment?.hourlyRate;
    } else if (payment.paymentType === "HALF_DAY") {
      totalLabourCost = payment?.halfDayRate;
    } else if (payment.paymentType === "FULL_DAY") {
      totalLabourCost = payment?.fullDayRate;
    }

    formik.setFieldValue("totalLabourCost", totalLabourCost);
  }, [formik.values.minimumHours]);

  useEffect(() => {
    const combinedHoursMinutes = toBase100Hours({
      hours: formik.values.nonBusinessHours.hour,
      minutes: formik.values.nonBusinessHours.minute,
    });

    const nonBusinessTotalLabourCost =
      combinedHoursMinutes * formik.values.nonBusinessHourRate;

    formik.setFieldValue(
      "nonBusinessTotalLabourCost",
      nonBusinessTotalLabourCost,
    );
  }, [formik.values.nonBusinessHours, formik.values.nonBusinessHourRate]);

  useEffect(() => {
    const combinedHoursMinutes = toBase100Hours({
      hours: formik.values.additionalHours.hour,
      minutes: formik.values.additionalHours.minute,
    });

    const totalNonBusinessPayment =
      combinedHoursMinutes * formik.values.additionalHourlyRate;

    formik.setFieldValue("totalNonBusinessPayment", totalNonBusinessPayment);
  }, [formik.values.additionalHours]);

  const handleSaveTimeLogButton = async () => {
    const payload = {
      minimumHours: toBase100Hours({
        hours: formik.values.minimumHours.hour,
        minutes: formik.values.minimumHours.minute,
      }),
      nonBusinessHourRate: isShowNBH ? formik.values.nonBusinessHourRate : 0,
      nonBusinessHours: isShowNBH
        ? toBase100Hours({
            hours: formik.values.nonBusinessHours.hour,
            minutes: formik.values.nonBusinessHours.minute,
          })
        : 0,
      additionalHourlyRate: formik.values.additionalHourlyRate,
      additionalHours: toBase100Hours({
        hours: formik.values.additionalHours.hour,
        minutes: formik.values.additionalHours.minute,
      }),
      workOrderId,
      type: "CLIENT",
    };

    await updateWorkOrderTimeLogMutation(payload);

    setIsEditClientApprovalTimeLogModalOpen(false);

    refetchworkOrderPaymentData();
  };

  const initialAdditionalRef = useRef(
    cloneDeep(formik.values.additionalHours) || { hour: 0, minute: 0 },
  );

  return (
    <BaseModal
      className="overflow-auto "
      open={isEditClientApprovalTimeLogModalOpen}
      close={() => setIsEditClientApprovalTimeLogModalOpen(false)}
    >
      <div className="py-3 sm:py-7 px-4 sm:px-8 w-full sm:w-[800px] lg:w-[1000px]">
        <div className="flex items-center border-bottom mb-4 pb-1">
          <h5 className="text-dark">Edit Time Log</h5>
        </div>
        <div className="border rounded-md p-4 sm:p-5">
          <div className="border-b pb-1 flex flex-wrap gap-y-1 items-center justify-between">
            <p className="!text-secondary font-medium text-base">
              Client Rate ({PaymentType[payment.paymentType]})
            </p>
            <p className="!text-secondary font-medium text-base">
              {displayFormattedTotalTimelog(payment?.hoursWorked)}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 sm:gap-x-10 gap-y-4 sm:gap-y-8 mt-4 items-center">
            <BaseTextField
              label={
                PaymentType[payment.paymentType]
                /* (payment.paymentType === "FIXED" && "Fixed Rate") ||
                (payment.paymentType === "HOURLY" && "Hourly Rate") ||
                (payment.paymentType === "HALF_DAY" && "Half Day Rate") ||
                (payment.paymentType === "FULL_DAY" && "Full Day Rate") ||
                (payment.paymentType === "FTH" && "First Two Hours Rate") || (payment.paymentType === "CUSTOM_HOURS" &&"Custom Hours") */
              }
              labelShrink={false}
              value={formik?.values?.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              // error={formik.touched.clientName && formik.errors.clientName}
              // helperText={formik.touched.clientName && formik.errors.clientName}

              optional={false}
              isDefaultField={false}
              disabled={true}
            />
            <div>
              <p className="font-normal mb-3">Minimum Hour Time</p>
              <BaseHourMinutePicker
                disabled={true}
                className="h-10"
                name="minimumHours"
                hour={formik?.values?.minimumHours.hour}
                minute={formik?.values?.minimumHours.minute}
                onChange={(e) => {
                  // const formatted = dayjs(e).format("h:mm").replace(":", ".");
                  console.log("selected value", e);
                  formik.setFieldValue("minimumHours", e);
                }}
              />
            </div>
            <BaseTextField
              label="Total Business Payment"
              labelShrink={false}
              value={formik?.values?.totalLabourCost}
              // onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              // error={formik.touched.clientName && formik.errors.clientName}
              // helperText={formik.touched.clientName && formik.errors.clientName}

              optional={false}
              isDefaultField={false}
              disabled={true}
            />

            <BaseTextField
              label="Additional Hourly Rate"
              labelShrink={false}
              value={formik?.values?.additionalHourlyRate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              // error={formik.touched.clientName && formik.errors.clientName}
              // helperText={formik.touched.clientName && formik.errors.clientName}

              optional={false}
              isDefaultField={false}
              disabled={true}
            />
            <div>
              <p className="font-normal mb-3">Additional Hour Time</p>
              <BaseHourMinutePicker
                className="h-10"
                name="additionalHours"
                hour={formik?.values?.additionalHours.hour}
                minute={formik?.values?.additionalHours.minute}
                onChange={(e) => {
                  // const formatted = dayjs(e).format("h:mm").replace(":", ".");
                  console.log("selected value", e);
                  formik.setFieldValue("additionalHours", e);
                }}
              />
            </div>
            <BaseTextField
              label="Total Business Payment"
              labelShrink={false}
              value={formik?.values?.totalNonBusinessPayment}
              // onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              // error={formik.touched.clientName && formik.errors.clientName}
              // helperText={formik.touched.clientName && formik.errors.clientName}

              optional={false}
              isDefaultField={false}
              disabled={true}
            />
            <div>
              <div className="flex items-center mb-2">
                <p className="font-normal">
                  Rate of NBH{" "}
                  <span className="text-[10px]">(None-Business Hour)</span>
                </p>
                <div className="ml-2">
                  <BaseCheckbox
                    id="enableEngineerAdditionalHourRate"
                    name="enableEngineerAdditionalHourRate"
                    checked={isShowNBH}
                    onChange={(e) => {
                      setIsShowNBH(e.target.checked);
                    }}
                  />
                </div>
              </div>
              {isShowNBH && (
                <BaseTextField
                  id="nonBusinessHourRate"
                  name="nonBusinessHourRate"
                  labelShrink={false}
                  value={formik?.values?.nonBusinessHourRate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  optional={false}
                  isDefaultField={false}
                />
              )}
            </div>
            {isShowNBH && (
              <div>
                <p className="font-normal mb-3">Non-Business Hour Time</p>
                <BaseHourMinutePicker
                  className="h-10"
                  name="nonBusinessHours"
                  hour={formik?.values?.nonBusinessHours.hour}
                  minute={formik?.values?.nonBusinessHours.minute}
                  onChange={(e) => {
                    const nonBusiness = e || { hour: 0, minute: 0 };

                    // ✅ Use current value if it's different from the initial one
                    const baseline = formik.values.additionalHoursChanged
                      ? cloneDeep(formik.values.additionalHours)
                      : initialAdditionalRef.current;

                    const totalMinutes = baseline.hour * 60 + baseline.minute;
                    const nonBusinessMinutes =
                      (nonBusiness.hour || 0) * 60 + (nonBusiness.minute || 0);

                    let remainingMinutes = totalMinutes - nonBusinessMinutes;
                    if (remainingMinutes < 0) remainingMinutes = 0;

                    const newHours = Math.floor(remainingMinutes / 60);
                    const newMinutes = remainingMinutes % 60;

                    // ✅ Update Formik
                    formik.setFieldValue("nonBusinessHours", nonBusiness);
                    formik.setFieldValue("additionalHours", {
                      hour: newHours,
                      minute: newMinutes,
                    });
                    formik.setFieldValue("additionalHoursChanged", true); // mark as changed
                  }}
                />
              </div>
            )}

            {isShowNBH && (
              <BaseTextField
                label={
                  <p className="font-normal">
                    NBH
                    <span className="text-[10px]"> (None-Business Hour) </span>
                    Payment
                  </p>
                }
                labelShrink={false}
                value={formik?.values?.nonBusinessTotalLabourCost}
                optional={false}
                isDefaultField={false}
                disabled={true}
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-end bg-white py-3 sm:py-6 mt-2 sm:mt-3">
          <BaseButton
            variant="outlined"
            // disabled={isEngineerFeedbackApiLoading}
            // isLoading={isEngineerFeedbackApiLoading}

            className="py-[4px] px-[28px] ml-4 rounded-none"
            color="primary"
            fullWidth={false}
            onClick={() => setIsEditClientApprovalTimeLogModalOpen(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            className="py-[5px] px-[28px] ml-4 rounded-none"
            color="primary"
            fullWidth={false}
            onClick={formik.handleSubmit}
            disabled={!isFormChanged}
          >
            Save
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default EditClientApprovalTimeLogModal;
